import type { RGroupRevealRequest } from './MarkushSearchSection';
import { atomListFromTool, atomListTool, isMarkushDrawingJson } from '@peruselab/structure-editor/domain/markushLayers';
import type { DrawingDocument, NormalizedPoint } from '@peruselab/structure-editor/model';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ChangeEvent,
  type MouseEvent as ReactMouseEvent
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUnsavedDraft, type DraftContent } from '../../hooks/useUnsavedDraft';
import type { UnsavedDraft } from '../../hooks/unsavedDraftStore';
import { UnsavedDraftBanner } from './UnsavedDraftBanner';
import { useTranslation } from 'react-i18next';
import type { PubChemMatch, SearchResults } from '@peruselab/api-client/moleculeSearch';
import {
  DrawingPaper,
  ESCAPE_PRIORITY,
  useEscapeAction,
  type ClientToPaperPoint,
  type TextToolbarControls
} from '@peruselab/structure-editor/components';
import { TextFormatToolbar } from '../../components/TextFormatToolbar';
import { moleculeCardDepiction } from '../../components/moleculeCardDepiction';
import { usePaperDropTargets } from '../../dnd/usePaperDropTargets';
import { usePaperAdditions } from '../../hooks/usePaperAdditions';
import { clampPaperSpan, usePaperSpan } from '../../hooks/usePaperSpan';
import { PaperResizeHandle, type MosaicMeasure } from '../../components/PaperResizeHandle';
import { PaperAdditionsContext } from '../../components/panels/paperAdditions';
import {
  DEFAULT_BOND_DRAWING_CONFIG,
  DEFAULT_INK_COLOR,
  type BondDrawingConfig
} from '@peruselab/structure-editor/domain/bonds';
import type { PaperSize } from '@peruselab/structure-editor/domain/geometry';
import { emptySelection } from '@peruselab/structure-editor/domain/editor';
import { validateDrawingDocument } from '@peruselab/structure-editor/domain/graphDrawing';
import { atomStereoValues, cipResult } from '@peruselab/structure-editor/domain/documentSelectors';
import { downloadText } from '@peruselab/structure-editor/domain/download';
import { drawingDocumentToSvg, type SvgRenderOptions } from '@peruselab/structure-editor/rendering/svgExport';
import { printElement } from '../../printing/printElement';
import { prepareExport, type ExportSource } from '../../export/exportPipeline';
import { useSystemClipboard } from '../../hooks/useSystemClipboard';
import { defaultSettings } from '../../export/exportModel';
import { SaveDrawingDialog } from './SaveDrawingDialog';
import { ExportDialog } from '../../export/ExportDialog';
import { useExportFileStem } from '../../export/exportFileStem';
import { pageOf } from '@peruselab/structure-editor/domain/page';
import { toChemMoleculeV1 } from '@peruselab/structure-editor/domain/moleculeProjection';
import { cleanUpStructures } from '@peruselab/structure-editor/chemistry/structureCleanup';
import { getMolecule } from '@peruselab/api-client/molecules';
import { useDrawingDocument } from '../../hooks/useDrawingDocument';
import { useChemicalValidation } from '../../hooks/useChemicalValidation';
import { usePartialCharges } from '../../hooks/usePartialCharges';
import { useMoleculeSearch } from '../../hooks/useMoleculeSearch';
import { useReactionSearch } from '../../hooks/useReactionSearch';
import { ReactionConditionsCard } from '../../components/panels/ReactionConditionsCard';
import { useReactionTextReading } from '../../hooks/useReactionTextReading';
import { annotationBounds } from '@peruselab/structure-editor/domain/objectAlignment';
import { conditionsFromFields } from '../../hooks/reactionConditions';
import { documentArrowOf, paperFromReaction, reactionDocumentFromPaper } from '../../hooks/reactionOnPaper';
import { useTextSearch } from '../../hooks/useTextSearch';
import { useSearchResultFilters } from '../../hooks/useSearchResultFilters';
import { useMoleculeFormulas } from '../../hooks/useMoleculeFormulas';
import { intersectSearchResults } from '../../hooks/intersectSearchResults';
import type { SearchLike } from '../../hooks/searchRequestState';
import {
  drawingCards,
  documentCards,
  reactionCards,
  resultCards,
  resultSources,
  sortCards,
  type ResultEntry
} from '../../components/panels/searchResultsModel';
import { useSmilesImport } from '../../hooks/useSmilesImport';
import { usePubChemImport } from '../../hooks/usePubChemImport';
import { useOcsrImport } from '../../hooks/useOcsrImport';
import { reactionDetailsKey, useDrawingPersistence } from '../../hooks/useDrawingPersistence';
import { WorkspaceDiscussionPanel } from '../../features/discussions/WorkspaceDiscussionPanel';
import { useDiscussionCount } from '../../features/discussions/useDiscussionCount';
import { useRailCollapsed, useRightPanelCollapsed } from '../../hooks/useRailCollapsed';
import { Icon } from '../../components/Icon';
import { SiteHeader } from '../SiteHeader';
import { TopBar, type SearchMode } from './TopBar';
import { ToolRail } from './ToolRail';
import { EditorActionBar } from './EditorActionBar';
import { useToolbarOverflow } from './useToolbarOverflow';
import type { ViewControlGroup } from '@peruselab/structure-editor/components';
import { DocumentBar, MarkushNotice } from './DocumentBar';
import { MatchCountBadge } from './MatchCountBadge';
import { useMyDrawingsFeed } from '../../hooks/useMyDrawingsFeed';
import { useStyleDefaultsSync } from '../../hooks/useStyleDefaultsSync';
import type { MyDrawingsStatus } from '../../components/panels/MyDrawingsFilter';
import type { MoleculeDrawingSummary } from '@peruselab/api-client/moleculeDrawings';
import { PLUS_JOINED_ZONES, plusSignPositions, REACTION_ZONES } from '@peruselab/structure-editor/domain/reactionGraph';
import { FileDrawer, type ImportTarget } from './FileDrawer';
import { PeriodicTablePanel } from './PeriodicTablePanel';
import { RepeatUnitPopover } from './RepeatUnitPopover';
import { importMarkush } from '@peruselab/structure-editor/chemistry/markushImport';
import { MARKUSH_SEARCH_MAX_STRUCTURES } from '@peruselab/structure-editor/domain/markushSummary';
import { ResultsRegion } from './ResultsRegion';
import { OcsrSourceImageCard } from '../../components/panels/OcsrSourceImageCard';
import { RightPanel, type PanelTab } from './RightPanel';
import type { ComposerMode } from './ComposePanel';
import { FeedbackDisclosure } from './FeedbackDisclosure';
import { drawingProblems } from '../../components/panels/DrawingInfoPanel';
import { IllustrationComposer } from '../../components/IllustrationComposer';
import { BiopolymerComposer } from '../../components/BiopolymerComposer';
import { IllustrationViewer } from '../../components/IllustrationViewer';
import { DrawingViewer } from '../../components/DrawingViewer';
import { getMoleculeDrawing } from '@peruselab/api-client/moleculeDrawings';
import { useActiveTenant } from '../../tenancy/activeTenant';
import { useDrawnMoleculeRegistration } from '../../hooks/useDrawnMoleculeRegistration';
import { useStructureCheck } from '../../hooks/useStructureCheck';
import { ionicPairs } from '@peruselab/structure-editor/domain/ionicPairs';
import { haptoLigands } from '@peruselab/structure-editor/domain/haptoBonds';
import { agosticStarts } from '@peruselab/structure-editor/domain/drawingDocumentProjections';
import { structureIssueMessage } from '../../components/panels/StructureChecksSection';
import {
  confirmStructureWarning,
  withdrawStructureConfirmation,
  type StructureFix,
  type StructureIssue
} from '@peruselab/api-client/structureChecks';
import { publicReactionPath, usePublicAccess } from '../../features/public/publicAccess';

// ADR 0007 — the workspace shell: the drawing editor at /draw (P10e cutover). Three columns
// (tool rail / centre / tabbed FILTER·EDIT·COMPOSE panel) with a live drawing paper in the
// centre; the File drawer holds persistence + import/export.
// #756 -- the paper's text editor gets the app's icon toolbar.
const renderTextToolbar = (controls: TextToolbarControls) => <TextFormatToolbar controls={controls} />;

const NO_ISSUES: readonly StructureIssue[] = [];

export function WorkspaceShell() {
  const { t } = useTranslation();
  const [paperSize, setPaperSize] = useState<PaperSize>({ width: 1000, height: 700 });
  const [, setWebGlAvailable] = useState(false);
  const [panelTab, setPanelTab] = useState<PanelTab>('arrange');
  const [fileDrawerOpen, setFileDrawerOpen] = useState(false);
  const [saveNameOpen, setSaveNameOpen] = useState(false);
  // #1165: what was asked for while the paper held unsaved work -- done once it is saved or discarded.
  const [pendingReplacement, setPendingReplacement] = useState<{
    readonly reason: 'new' | 'open' | 'leave';
    readonly proceed: () => void;
  } | null>(null);
  const [toolbarSaveRequested, setToolbarSaveRequested] = useState(false);
  const [toolbarSaveMessage, setToolbarSaveMessage] = useState<string | null>(null);
  const [importTarget, setImportTarget] = useState<ImportTarget | null>(null);
  const fileReturnFocus = useRef<HTMLElement | null>(null);
  const exportReturnFocus = useRef<HTMLElement | null>(null);
  const [pendingCharge, setPendingCharge] = useState<number | null>(null);
  const [periodicTableOpen, setPeriodicTableOpen] = useState(false);
  // #654 -- non-null while the periodic table is picking a Markush atom list (multi-select).
  const [atomListDraft, setAtomListDraft] = useState<readonly number[] | null>(null);
  // #575/#576/#577/#578 — Edit tab "Tools" section toggles. Session-only, not persisted.
  const [showNodeNumbers, setShowNodeNumbers] = useState(false);
  // #1047, #780 -- "Show R/S | E/Z Labels": each stereocentre and defined double bond labelled on the paper.
  const [showStereoLabels, setShowStereoLabels] = useState(false);
  // ADR 0022 (as amended) -- a view option, like the R/S labels: the bolts between a salt's ions.
  const [showIonicBonds, setShowIonicBonds] = useState(false);
  const [showHapticity, setShowHapticity] = useState(false);
  const [showBondAcceptors, setShowBondAcceptors] = useState(false);
  const [showCenterOfRotation, setShowCenterOfRotation] = useState(false);
  const [lassoSelectionEnabled, setLassoSelectionEnabled] = useState(false);
  // #508 — portal target for PaperViewportControls, so it renders inline in the toolbar row
  // instead of structure-editor's default absolutely-positioned overlay on the canvas. A ref
  // alone wouldn't re-render when it first attaches, so this is state instead.
  const [viewportControlsSlot, setViewportControlsSlot] = useState<HTMLDivElement | null>(null);
  // #807 R4 -- measured overflow: groups the toolbar row has no room for move into ⋯ (the paper's
  // own view-control groups render their menu forms into ⋯ through overflowSlot).
  const toolbarRowRef = useRef<HTMLDivElement | null>(null);
  const hiddenToolbarGroups = useToolbarOverflow(toolbarRowRef);
  const hiddenViewGroups = useMemo(
    () =>
      new Set(
        [...hiddenToolbarGroups].filter((group): group is ViewControlGroup =>
          ['pen', 'zoom', 'snapping', 'page'].includes(group)
        )
      ),
    [hiddenToolbarGroups]
  );
  const [moreOpen, setMoreOpen] = useState(false);
  const closeMore = useCallback(() => setMoreOpen(false), []);
  const [moreTrigger, setMoreTrigger] = useState<HTMLButtonElement | null>(null);
  const [overflowSlot, setOverflowSlot] = useState<HTMLDivElement | null>(null);
  // Narrow viewports (<=960px): the rail and the right panel are off-canvas drawers, one open at
  // a time. Above that, both are always-visible grid columns and this stays null. (#383)
  const [mobileDrawer, setMobileDrawer] = useState<'rail' | 'panel' | null>(null);
  // #400 / #402 — the left rail and the right panel each collapse to an icon strip on desktop;
  // the choices are remembered per browser. At <= 960px each is an off-canvas drawer (#383):
  // the drawer always opens full (the persisted pref is not applied to it), and its collapse
  // chevron closes the drawer rather than touching the pref (#405).
  const [railCollapsedPref, setRailCollapsed] = useRailCollapsed();
  const [rightCollapsedPref, setRightCollapsed] = useRightPanelCollapsed();
  const railCollapsed = railCollapsedPref && mobileDrawer !== 'rail';
  const rightPanelCollapsed = rightCollapsedPref && mobileDrawer !== 'panel';
  // The rail/panel own one collapse control; route it to the drawer when that is what is open.
  const setRailCollapsedContextual = useCallback(
    (collapsed: boolean) => {
      if (mobileDrawer === 'rail') {
        if (collapsed) setMobileDrawer(null);
      } else {
        setRailCollapsed(collapsed);
      }
    },
    [mobileDrawer, setRailCollapsed]
  );
  const setRightCollapsedContextual = useCallback(
    (collapsed: boolean) => {
      if (mobileDrawer === 'panel') {
        if (collapsed) setMobileDrawer(null);
      } else {
        setRightCollapsed(collapsed);
      }
    },
    [mobileDrawer, setRightCollapsed]
  );
  // #680 -- the match badge's "some are hidden" action: the Filter tab, in the expanded panel (or
  // the panel drawer where the side columns are drawers, <= 960px -- see the CSS breakpoint).
  const showFilters = useCallback(() => {
    setPanelTab('filter');
    if (globalThis.matchMedia?.('(max-width: 960px)').matches) setMobileDrawer('panel');
    else setRightCollapsed(false);
  }, [setRightCollapsed]);
  // #1110 P1: Structure info's "All in Checks" -- the Checks list tops the Arrange tab (ADR 0020 D3).
  const showChecks = useCallback(() => {
    setPanelTab('arrange');
    if (globalThis.matchMedia?.('(max-width: 960px)').matches) setMobileDrawer('panel');
    else setRightCollapsed(false);
  }, [setRightCollapsed]);

  const [bondConfig, setBondConfig] = useState<BondDrawingConfig>(DEFAULT_BOND_DRAWING_CONFIG);
  const [viewMode, setViewMode] = useState<'editor' | ComposerMode>('editor');
  // #523: a reaction/illustration search-result match opens into a read-only viewer, not the
  // compose-new flow above -- separate state rather than reusing viewMode, since a viewer isn't a
  // ComposerMode (no participants to accumulate, no save action) and shouldn't share its render
  // branch with the interactive composers.
  const [viewingDocument, setViewingDocument] = useState<{
    readonly kind: 'illustration' | 'drawing';
    readonly id: string;
  } | null>(null);
  const fileButtonRef = useRef<HTMLButtonElement | null>(null);
  const composerRef = useRef<HTMLElement | null>(null);
  const composeTabRef = useRef<HTMLButtonElement | null>(null);
  const returningFromComposer = useRef(false);
  const clipboardSource = useRef<ExportSource | null>(null);
  const { copyToSystemClipboard, clipboardMessage } = useSystemClipboard(clipboardSource);
  const doc = useDrawingDocument(paperSize, copyToSystemClipboard);
  const liveChargeAtomId =
    !periodicTableOpen && doc.tool === 'select' && doc.singleAtomId && !doc.markushAtomLabels[doc.singleAtomId]
      ? doc.singleAtomId
      : undefined;
  const railCharge = liveChargeAtomId
    ? (doc.document.structure.formalCharges[liveChargeAtomId] ?? 0)
    : (pendingCharge ?? 0);
  const changePendingCharge = (value: number) => {
    setPendingCharge(value);
    if (liveChargeAtomId) {
      doc.applyAtomFormalCharge(liveChargeAtomId, value);
    } else if (!periodicTableOpen && !doc.tool.startsWith('element-')) {
      doc.setTool('charge-apply');
    }
  };

  // #862 -- new-object style defaults follow the user across devices (account preferences).
  const styleDefaultsStatus = useStyleDefaultsSync(doc.annotationStyleDefaults, doc.setAnnotationStyleDefaults);
  // #737 -- the paper's on-screen size in result cards, from its resize corner (never wider than
  // the mosaic has columns, which follows the window).
  const paperSpan = usePaperSpan(doc.isOversizedImport);
  const [mosaic, setMosaic] = useState<MosaicMeasure | null>(null);
  const mosaicColumns = mosaic?.columns ?? Number.POSITIVE_INFINITY;
  const shownPaperSpan = clampPaperSpan(paperSpan.span, mosaicColumns);
  // #753 -- the paper's exact height: N card rows plus the gaps between them, so its bottom edge
  // lines up with the cards beside it. (Its 16:9 x N aspect ratio, the fallback until the grid is
  // measured, leaves out 7/16 of each row gap -- about 4 px short at 2 rows.)
  const paperHeight =
    mosaic === null
      ? null
      : shownPaperSpan.rows * ((mosaic.track * 9) / 16) + (shownPaperSpan.rows - 1) * mosaic.rowGap;
  const paperSpanStyle = {
    '--paper-columns': shownPaperSpan.columns,
    '--paper-rows': shownPaperSpan.rows,
    ...(mosaic === null
      ? {}
      : {
          '--paper-width': `${shownPaperSpan.columns * mosaic.track + (shownPaperSpan.columns - 1) * mosaic.rowGap}px`
        }),
    ...(paperHeight === null ? {} : { '--paper-height': `${paperHeight}px` })
  } as CSSProperties;
  // #648 -- chemistry readouts see the drawing with Markush R atoms removed (identical to
  // doc.document outside Markush mode), so an R is never validated or charged as carbon.
  // #1110 (owner test): a fix previewed while the pointer is on its button is tentative -- only
  // the paper shows it. Checks, search and registration keep reading the drawing as it was, or the
  // re-check reshaped Structure info under the pointer, cancelling the preview, again and again.
  // The settled drawing is remembered while nothing is previewed; moving from one fix to the next
  // (cancel, then preview, in one event) keeps it.
  const [fixPreviewBase, setFixPreviewBase] = useState<DrawingDocument | null>(null);
  const settledChemistryRef = useRef(doc.chemistryDocument);
  if (fixPreviewBase === null) settledChemistryRef.current = doc.chemistryDocument;
  const settledChemistry = fixPreviewBase ?? doc.chemistryDocument;
  const validation = useChemicalValidation(settledChemistry, doc.applyWithoutHistory);
  const stereoLabels = useMemo(
    () =>
      showStereoLabels
        ? Object.fromEntries(
            Object.entries(atomStereoValues(doc.document)).flatMap(([atomId, value]) =>
              value.descriptor ? [[atomId, value.descriptor]] : []
            )
          )
        : undefined,
    [doc.document, showStereoLabels]
  );
  // #780 -- each defined double bond's E or Z, with the R/S labels (derived by the chemistry check).
  const bondStereoLabels = useMemo(
    () => (showStereoLabels ? { ...(cipResult(doc.document)?.bondDescriptors ?? {}) } : undefined),
    [doc.document, showStereoLabels]
  );
  const partialCharges = usePartialCharges(settledChemistry, doc.applyWithoutHistory);
  // #658 (Markush M4) -- in Markush mode a bounded, complete drawing within the cap is searched as a
  // Markush match (every structure it stands for); anything else keeps the core-only search, and the
  // note above the results says which ran and why.
  const markushCount = doc.markushStructureCount;
  // A drawing with no variable positions yet is just its scaffold: an ordinary structure search.
  const hasVariablePositions = doc.markushLayers.variablePositions.length > 0;
  const markushSearchable =
    doc.markushDocument !== null &&
    hasVariablePositions &&
    // #660: generic drawings (a class, an open repeat) are searchable too; the cap applies to their
    // finite combinations only.
    (markushCount?.kind === 'count' || markushCount?.kind === 'generic') &&
    markushCount.value > 0 &&
    markushCount.value <= MARKUSH_SEARCH_MAX_STRUCTURES;
  // #701: every molecule on the paper is searched on its own (useMoleculeSearch -> queryMolecules),
  // loose or in a reaction box -- this replaced ADR 0014 Q1's "the selected or last-edited one".
  const searchDocument = settledChemistry;
  // #704: with a reaction arrow on the paper, a reaction search from what is in its boxes.
  const drawnArrow = doc.document.annotations.find((annotation) => annotation.kind === 'reaction-arrow');
  const reactionSearch = useReactionSearch(
    settledChemistry,
    doc.reactionMolecules,
    // The saved kind for the drawn arrow (#705): an unequal equilibrium finds saved equilibria.
    doc.hasReactionArrow && drawnArrow?.kind === 'reaction-arrow' ? documentArrowOf(drawnArrow).kind : null
  );
  const search = useMoleculeSearch(
    searchDocument,
    undefined,
    markushSearchable && doc.markushDocument ? { document: doc.markushDocument } : null
  );
  const markushSearchNote = !hasVariablePositions
    ? undefined
    : markushSearchable && markushCount?.kind === 'count'
      ? t('markushSearch.active', { count: markushCount.value })
      : markushSearchable && markushCount?.kind === 'generic'
        ? t('markushSearch.generic', { count: markushCount.value })
        : markushCount && markushCount.kind !== 'incomplete' && markushCount.value > MARKUSH_SEARCH_MAX_STRUCTURES
          ? t('markushSearch.tooMany', { count: markushCount.value, max: MARKUSH_SEARCH_MAX_STRUCTURES })
          : t('markushSearch.incomplete');
  const hasNodes = doc.document.structure.graph.nodes.length > 0;
  // #743 (owner): the arrow alone on the paper is the whole query -- only reactions of its kind.
  const arrowOnly = !hasNodes && doc.document.annotations.length === 1 && doc.hasReactionArrow;

  // #463: Text / Text+Drawing search. mode/textQuery are controlled state (TopBar is purely
  // presentational) so the top-bar dropdown and this results region can share one source of
  // truth. committedTextResults is the "page" ResultsRegion shows in Text mode -- it only
  // updates on Enter/selecting a non-openable suggestion, independent of the dropdown, which
  // always tracks the live (debounced) query as the user types.
  const [searchMode, setSearchMode] = useState<SearchMode>('drawing');
  const [textQuery, setTextQuery] = useState('');
  const textSearch = useTextSearch(textQuery, searchMode, search.reactionBuckets, search.structuralConstraints);
  const [committedTextResults, setCommittedTextResults] = useState<SearchResults | null>(null);
  const trimmedTextQuery = textQuery.trim();

  const textDrawingResults = useMemo(
    () => intersectSearchResults(search.currentResults, textSearch.currentResults, hasNodes, trimmedTextQuery !== ''),
    [search.currentResults, textSearch.currentResults, hasNodes, trimmedTextQuery]
  );

  const suggestionFormulas = useMoleculeFormulas(textSearch.currentResults);
  const suggestions = useMemo(() => {
    if (!textSearch.currentResults) return [];
    // #688 -- one suggestion per molecule, like the result cards. #746 -- plus the reactions found
    // by their own words (title, participants' names, notes), in the same relevance order.
    const reactions = reactionCards(
      resultSources(textSearch.currentResults).filter((source) => source.standalone),
      suggestionFormulas
    );
    return sortCards<ResultEntry>(
      [
        ...resultCards(textSearch.currentResults, suggestionFormulas),
        ...reactions,
        ...drawingCards(resultSources(textSearch.currentResults).filter((source) => source.standalone)),
        ...documentCards(resultSources(textSearch.currentResults))
      ],
      'scoreDesc'
    ).slice(0, 5);
  }, [textSearch.currentResults, suggestionFormulas]);

  // #485: SearchStatusHeader's Retry button (shown once the 'error' passthrough below lets an
  // error reach it) must actually re-issue the request, not just re-snapshot currentResults --
  // that's still null right after an error. Retrying re-runs the real fetch and, once it lands,
  // commits it as the page -- but only if it's still an answer to the query that was retried
  // (`sourceKey` guard), so a query typed while the retry was in flight doesn't get silently
  // auto-committed too.
  const pendingRetryKeyRef = useRef<string | null>(null);

  const commitTextSearch = useCallback(() => {
    pendingRetryKeyRef.current = null;
    setCommittedTextResults(textSearch.currentResults);
  }, [textSearch.currentResults]);

  const retryTextSearch = useCallback(async () => {
    pendingRetryKeyRef.current = trimmedTextQuery;
    await textSearch.runSearch();
  }, [textSearch, trimmedTextQuery]);
  useEffect(() => {
    if (
      pendingRetryKeyRef.current !== null &&
      textSearch.search.state === 'ready' &&
      textSearch.search.sourceKey === pendingRetryKeyRef.current
    ) {
      pendingRetryKeyRef.current = null;
      setCommittedTextResults(textSearch.search.results);
    }
  }, [textSearch.search]);

  const textSearchView: SearchLike<SearchResults> = {
    search:
      committedTextResults !== null
        ? { state: 'ready', sourceKey: trimmedTextQuery, results: committedTextResults }
        : // #485: 'error' must pass through here too -- collapsing it to 'idle' silently discarded
          // a failed search instead of letting SearchStatusHeader render it.
          textSearch.search.state === 'loading' || textSearch.search.state === 'error'
          ? textSearch.search
          : { state: 'idle' },
    currentResults: committedTextResults,
    // #678: never "stale" -- live results differing from the committed page just mean the user
    // hasn't pressed Enter (or picked a suggestion) yet, not that anything is loading. Reporting it
    // kept SearchStatusHeader's progress bar running forever with no request in flight (e.g. after
    // picking the PubChem row). A real in-flight request still shows via the 'loading' passthrough.
    searchIsStale: false,
    runSearch: retryTextSearch
  };
  const textDrawingView: SearchLike<SearchResults> = {
    search:
      textDrawingResults !== null
        ? { state: 'ready', sourceKey: 'textDrawing', results: textDrawingResults }
        : search.search.state === 'loading' || textSearch.search.state === 'loading'
          ? { state: 'loading', sourceKey: 'textDrawing' }
          : { state: 'idle' },
    currentResults: textDrawingResults,
    // Always live (both halves re-run continuously as the canvas/query change) -- there's no
    // "committed" snapshot for this mode to fall behind, unlike pure Text mode.
    searchIsStale: false,
    runSearch: async () => {
      await Promise.all([search.runSearch(), textSearch.runSearch()]);
    }
  };
  const activeSearch: SearchLike<SearchResults> =
    searchMode === 'drawing' ? search : searchMode === 'text' ? textSearchView : textDrawingView;
  // #682 -- an empty canvas in Drawing mode (sign-in, refresh, New drawing, cleared) shows the
  // user's own drawings in the results area instead of an empty page; drawing anything switches to
  // structure-search results, and clearing brings the feed back (re-fetched).
  const myDrawingsActive =
    searchMode === 'drawing' &&
    !hasNodes &&
    doc.document.annotations.length === 0 &&
    viewMode === 'editor' &&
    viewingDocument === null;
  // #837 -- the feed's filter (Filter tab): Active, or the Archived drawings with Restore.
  const [myDrawingsStatus, setMyDrawingsStatus] = useState<MyDrawingsStatus>('active');
  const tenant = useActiveTenant();
  const myDrawings = useMyDrawingsFeed(myDrawingsActive, myDrawingsStatus, tenant.scopeKey);
  // #1064 (ADR 0019 D5): each molecule is registered for the user's space once drawn -- not for a
  // view-only role, nor in Markush mode (a scaffold isn't a molecule, O4).
  const { signedIn } = usePublicAccess();
  // ADR 0020 D1 (#1099): the server's structure check is the authority once it has answered for
  // the drawing as it is; until then (or signed out, owner: "as today") the browser's check stands.
  // #1101: paused while a fix is previewed, so the hovered entry stays put.
  const structureCheck = useStructureCheck(
    settledChemistry,
    signedIn && doc.markushDocument === null,
    fixPreviewBase !== null
  );
  const drawnRegistration = useDrawnMoleculeRegistration(
    settledChemistry,
    structureCheck.marks
      ? // D4: structures with errors aren't registered; warnings still are (D4's open half).
        { invalidAtomIds: structureCheck.marks.errorAtomIds, invalidBondIds: structureCheck.marks.errorBondIds }
      : validation.currentValidation && (structureCheck.failed || !signedIn)
        ? { invalidAtomIds: validation.invalidAtomIds, invalidBondIds: validation.invalidBondIds }
        : null,
    tenant.scopeKey,
    signedIn && tenant.canEdit && doc.markushDocument === null
  );
  // #1103: a structure is registered only once the server's check has answered (#1099), which is
  // usually after the drawing's first search -- so that search, and the 60 s it was reused for,
  // couldn't find the structure itself. Each registration that answers asks the search again.
  // (Read through a ref: `runSearch` changes with every edit, and only a registration should ask.)
  const researchDrawing = useRef(search.runSearch);
  useEffect(() => {
    researchDrawing.current = search.runSearch;
  });
  const registrations = drawnRegistration.registered;
  useEffect(() => {
    if (registrations > 0) void researchDrawing.current();
  }, [registrations]);
  const displayedResults = searchMode === 'drawing' ? search.displayResults : activeSearch.currentResults;
  const showingPreviousResults = activeSearch.currentResults === null && displayedResults !== null;
  const filters = useSearchResultFilters(displayedResults);

  // #1183 -- OCSR original source image comparison card beside the drawing paper.
  const [ocsrSourceImage, setOcsrSourceImage] = useState<{ url: string; name: string } | null>(null);
  const [ocsrImageExpanded, setOcsrImageExpanded] = useState(true);
  const dismissOcsrSourceImage = useCallback(() => {
    setOcsrSourceImage((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
  }, []);
  useEffect(() => {
    return () => {
      setOcsrSourceImage((prev) => {
        if (prev?.url) URL.revokeObjectURL(prev.url);
        return null;
      });
    };
  }, []);

  // Save / load / import, rehomed from ChemicalStructureEditor into the File drawer (P8).
  const onImported = useCallback(() => {
    dismissOcsrSourceImage();
    doc.setSelection(emptySelection());
    doc.setTool('select');
  }, [dismissOcsrSourceImage, doc.setSelection, doc.setTool]);
  const smilesImport = useSmilesImport(paperSize, doc.commitImportedDocument, onImported);
  const pubChemImport = usePubChemImport(smilesImport);
  const persistence = useDrawingPersistence(
    doc.document,
    doc.replaceDocument,
    reactionDetailsKey(doc.conditionFields, doc.catalystAtomIds)
  );
  const { changeArchiveStatus } = persistence;
  // ADR 0016 part C: the Discuss tab's badge, for the drawing as last saved.
  const discussionCount = useDiscussionCount(
    persistence.remoteDrawing?.id ?? null,
    persistence.remoteDrawing?.moleculeId ?? null
  );
  // #1189: Export file stem follows the hierarchy: user drawing title > molecule name > formula > default.
  const exportFileStem = useExportFileStem({
    document: doc.document,
    drawingTitle: persistence.drawingTitle,
    moleculeId: persistence.remoteDrawing?.moleculeId ?? null,
    currentResults: search.currentResults,
    hasMarkushContent: doc.hasMarkushContent
  });
  const { refresh: refreshMyDrawings } = myDrawings;
  const restoreDrawing = useCallback(
    async (drawing: MoleculeDrawingSummary) => {
      if (await changeArchiveStatus(drawing.id, drawing.documentRevision, false)) refreshMyDrawings();
    },
    [changeArchiveStatus, refreshMyDrawings]
  );

  // #465: a PubChem fallback row has no local record to open or commit -- selecting it imports
  // the compound via the same SMILES-import pipeline a manual PubChem lookup already uses.
  // #485: that pipeline's smilesImportState is shared with the File drawer's manual-paste panel,
  // the PubChem-lookup panel, and OCSR import -- a stale error/loading value from any of those
  // must not flash in the search dropdown just because the user later focuses it. This flag marks
  // "the currently-shown smilesImportState actually answers a click made from here," set on
  // click and cleared on the next query edit (see onQueryChange below).
  const [pubchemImportFromSearch, setPubchemImportFromSearch] = useState(false);
  const selectPubChemFallback = useCallback(
    (match: PubChemMatch) => {
      setPubchemImportFromSearch(true);
      // #678: like picking any other suggestion, this commits the query's results as the page.
      setCommittedTextResults(textSearch.currentResults);
      void smilesImport.importSmilesString(match.smiles);
    },
    [smilesImport, textSearch.currentResults]
  );

  // #415 -- loading a molecule-kind Templates favourite: fetch the bare Molecule and lay out a
  // fresh 2D depiction, then commit it like a SMILES import (a new, unsaved structure).
  const loadMoleculeTemplate = useCallback(
    async (moleculeId: string) => {
      const molecule = await getMolecule(moleculeId);
      const document = await moleculeCardDepiction(moleculeId, molecule.document, paperSize);
      doc.commitImportedDocument(document, paperSize);
      onImported();
    },
    [doc.commitImportedDocument, onImported, paperSize]
  );

  // #699/#700 -- the paper and its reaction boxes take dropped structures (usePaperDropTargets).
  const clientToPaper = useRef<ClientToPaperPoint>(null);
  const {
    paper: { setNode: setPaperDropNode, canDrop: paperCanDrop, isOver: paperDropOver },
    boxRefs,
    boxStates
  } = usePaperDropTargets(clientToPaper);
  const { reactants: reactantsDrop, products: productsDrop, above: aboveDrop, below: belowDrop } = boxStates;

  // #1137 (ADR 0014 amendment): what the texts by the arrow say, read as they are typed.
  const textReading = useReactionTextReading(doc.document, doc.reactionBoxesShown);
  // ADR 0014 P1 -- the reaction zones around the drawing's arrow, labelled for the paper.
  const reactionZones = useMemo(() => {
    if (!doc.reactionZonesLayout) return null;
    const dropStates = { reactants: reactantsDrop, products: productsDrop, above: aboveDrop, below: belowDrop };
    // #1137 -- a mark under each reagent typed by the arrow, just below its text.
    const textBounds = annotationBounds(doc.document);
    const marksIn = (zone: string) =>
      textReading.reagents.flatMap((reagent) => {
        const box = reagent.zone === zone ? textBounds.get(reagent.annotationId) : undefined;
        if (!box) return [];
        const at = doc.reactionZonesLayout!.toPixels({ x: (box.minX + box.maxX) / 2, y: box.maxY });
        return [{ x: at.x, y: at.y + 6, found: reagent.lookup.status === 'found' }];
      });
    return REACTION_ZONES.map((zone) => {
      const { rect, empty, molecules, plusSigns } = doc.reactionZonesLayout!.zones[zone];
      const label = t(`reactionZones.${zone}`);
      return {
        zone,
        rect,
        empty,
        label,
        molecules,
        plusSigns,
        textMarks: marksIn(zone),
        atomIds: molecules.flat(),
        dropState: dropStates[zone]
      };
    });
  }, [aboveDrop, belowDrop, doc.document, doc.reactionZonesLayout, productsDrop, reactantsDrop, t, textReading]);
  // One reaction arrow per drawing: once it is placed, a still-armed scheme-arrow tool hands
  // back to Select (the rail disables those tools while the arrow exists).
  const { hasReactionArrow, tool: activeTool, setTool: setActiveTool } = doc;
  useEffect(() => {
    if (hasReactionArrow && activeTool.startsWith('reaction-arrow-')) setActiveTool('select');
  }, [hasReactionArrow, activeTool, setActiveTool]);

  // #689/#700 -- what the result cards and My drawings put on the paper (a molecule, isomer-aware;
  // a drawing's structure; an illustration's), shared with them through PaperAdditionsContext.
  const paperAdditions = usePaperAdditions(doc.addStructures, paperSize, hasReactionArrow, doc.placeReaction);

  // #885: "Add to paper" from a public drawing's page arrives as /draw?addDrawing=<id> -- add it
  // once, then drop the parameter so a reload doesn't add it again.
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingAddDrawing = searchParams.get('addDrawing');
  const addedFromLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingAddDrawing || addedFromLinkRef.current === pendingAddDrawing) return;
    addedFromLinkRef.current = pendingAddDrawing;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('addDrawing');
        return next;
      },
      { replace: true }
    );
    void paperAdditions.addDrawing(pendingAddDrawing).catch((error: unknown) => {
      console.error('Add to paper from a public drawing failed', error);
    });
  }, [pendingAddDrawing, paperAdditions, setSearchParams]);

  // #1124: "Add to paper" from a public reaction's page arrives as /draw?addReaction=<id> -- the
  // whole reaction, once, as from search (#744).
  const pendingAddReaction = searchParams.get('addReaction');
  const addedReactionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingAddReaction || addedReactionRef.current === pendingAddReaction) return;
    addedReactionRef.current = pendingAddReaction;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('addReaction');
        return next;
      },
      { replace: true }
    );
    void paperAdditions.addReaction(pendingAddReaction).catch((error: unknown) => {
      console.error('Add to paper from a public reaction failed', error);
    });
  }, [pendingAddReaction, paperAdditions, setSearchParams]);

  // #929: "Open in workspace" on a Monograph arrives as /draw?addMolecule=<id> -- the molecule onto
  // the paper, once. A molecule with several isomers places the first (its picker is in search).
  const pendingAddMolecule = searchParams.get('addMolecule');
  const addedMoleculeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pendingAddMolecule || addedMoleculeRef.current === pendingAddMolecule) return;
    addedMoleculeRef.current = pendingAddMolecule;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('addMolecule');
        return next;
      },
      { replace: true }
    );
    void paperAdditions
      .addMolecule(pendingAddMolecule)
      .then((isomers) => {
        const first = isomers?.[0];
        return first ? paperAdditions.addMolecule(pendingAddMolecule, first.id) : null;
      })
      .catch((error: unknown) => {
        console.error('Open in workspace from a Monograph failed', error);
      });
  }, [pendingAddMolecule, paperAdditions, setSearchParams]);

  // #705 (ADR 0014 §5): Save keeps a drawing with a reaction arrow as a reaction -- its boxed
  // molecules registered and given their roles, the arrow, the Conditions card -- and a saved
  // reaction opens back onto the paper.
  const { replaceDocument: replaceDrawingDocument, loadReactionDetails } = doc;
  const saveCurrentDrawing = useCallback(
    (title?: string) => {
      const arrow = doc.document.annotations.find((annotation) => annotation.kind === 'reaction-arrow');
      if (!doc.hasReactionArrow || arrow?.kind !== 'reaction-arrow') {
        return persistence.saveDrawing(title);
      }
      // #1134: a structure in no box isn't part of the reaction -- say so rather than leave it
      // out of what is saved without a word.
      const loose = doc.reactionMolecules.filter((molecule) => molecule.zone === null).length;
      if (loose > 0 && !globalThis.confirm(t('persistencePanel.messages.looseStructures', { count: loose }))) {
        return Promise.resolve(false);
      }
      const paperReaction = {
        document: doc.document,
        molecules: doc.reactionMolecules,
        isCatalyst: doc.isCatalyst,
        arrow,
        conditions: conditionsFromFields(doc.conditionFields),
        reading: textReading
      };
      return persistence.saveReaction(() => reactionDocumentFromPaper(paperReaction), title);
    },
    [
      doc.conditionFields,
      doc.document,
      doc.hasReactionArrow,
      doc.isCatalyst,
      doc.reactionMolecules,
      paperSize,
      persistence,
      t,
      textReading
    ]
  );
  // #1165: anything that replaces or leaves the paper while it holds unsaved work asks first --
  // save, discard, or stay. A save or load still under way counts as unsaved.
  const hasUnsavedPaper =
    (doc.document.structure.graph.nodes.length > 0 ||
      doc.document.annotations.length > 0 ||
      persistence.drawingTitle.trim().length > 0) &&
    persistence.hasUnsavedChanges;
  const hasUnsavedWork =
    hasUnsavedPaper || persistence.persistence.state === 'loading' || persistence.persistence.state === 'saving';
  // #1165 layer 2: the unsaved paper is kept on this device, to be offered back after a refresh or
  // a crash -- with the drawing or reaction it was opened from, and a reaction's details.
  const draftContent = useMemo<DraftContent | null>(() => {
    if (!hasUnsavedPaper) return null;
    const { remoteDrawing, remoteReaction } = persistence;
    return {
      title: persistence.drawingTitle,
      document: doc.document,
      conditionFields: doc.conditionFields,
      catalystAtomIds: [...doc.catalystAtomIds],
      ...(remoteDrawing
        ? { from: { kind: 'drawing' as const, id: remoteDrawing.id, revision: remoteDrawing.documentRevision } }
        : remoteReaction
          ? { from: { kind: 'reaction' as const, id: remoteReaction.id, revision: remoteReaction.documentRevision } }
          : {})
    };
  }, [
    doc.catalystAtomIds,
    doc.conditionFields,
    doc.document,
    hasUnsavedPaper,
    persistence.drawingTitle,
    persistence.remoteDrawing,
    persistence.remoteReaction
  ]);
  const unsavedDraft = useUnsavedDraft(tenant.scopeKey, draftContent);
  /** Runs `proceed` now, or asks first and returns false. */
  const confirmBeforeReplacing = useCallback(
    (reason: 'new' | 'open' | 'leave', proceed: () => void): boolean => {
      if (!hasUnsavedWork) {
        proceed();
        return true;
      }
      setPendingReplacement({ reason, proceed });
      setFileDrawerOpen(false);
      return false;
    },
    [hasUnsavedWork]
  );
  // #1165: a refresh, a closed tab or a page served outside the app gets the browser's own question.
  useEffect(() => {
    if (!hasUnsavedWork) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedWork]);

  const { openReaction } = persistence;
  const navigate = useNavigate();
  // #1165: a link within the app (the logo, My comments, Organisations) leaves the paper without
  // unloading the page, so the browser never asks: these ask here. The help pages are served
  // whole, not by the app's router, so the browser's question covers them.
  const guardLinksAway = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!hasUnsavedWork || event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element).closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      if (url.pathname.startsWith('/help')) return;
      event.preventDefault();
      event.stopPropagation();
      confirmBeforeReplacing('leave', () => void navigate(url.pathname + url.search + url.hash));
    },
    [confirmBeforeReplacing, hasUnsavedWork, navigate]
  );
  /** #1165 layer 2: the kept draft back on the paper, on top of the drawing or reaction it came from. */
  const restoreDraft = useCallback(
    async (draft: UnsavedDraft) => {
      dismissOcsrSourceImage();
      let newerElsewhere = false;
      if (draft.from?.kind === 'drawing') {
        const saved = await getMoleculeDrawing(draft.from.id).catch(() => null);
        if (saved) {
          await persistence.loadDrawingById(draft.from.id);
          newerElsewhere = saved.documentRevision > draft.from.revision;
        } else persistence.resetForNewDrawing();
      } else if (draft.from?.kind === 'reaction') {
        const from = draft.from;
        // The reaction as saved is laid out first, so the draft on top reads as unsaved changes.
        await openReaction(from.id, async (reaction) => {
          newerElsewhere = reaction.documentRevision > from.revision;
          const laid = await paperFromReaction(reaction, paperSize);
          return { document: laid.document, details: reactionDetailsKey(laid.conditions, laid.catalystAtomIds) };
        });
      } else persistence.resetForNewDrawing();
      replaceDrawingDocument(draft.document);
      loadReactionDetails(draft.conditionFields, draft.catalystAtomIds);
      persistence.updateDrawingTitle(draft.title);
      setToolbarSaveMessage(t(newerElsewhere ? 'unsavedDraft.newerElsewhere' : 'unsavedDraft.restored'));
    },
    [dismissOcsrSourceImage, loadReactionDetails, openReaction, paperSize, persistence, replaceDrawingDocument, t]
  );

  const openReactionOnPaper = useCallback(
    (id: string) =>
      confirmBeforeReplacing('open', () => {
        dismissOcsrSourceImage();
        void openReaction(
          id,
          async (reaction) => {
            const laid = await paperFromReaction(reaction, paperSize);
            replaceDrawingDocument(laid.document);
            loadReactionDetails(laid.conditions, laid.catalystAtomIds);
            return { document: laid.document, details: reactionDetailsKey(laid.conditions, laid.catalystAtomIds) };
          },
          // #1136: someone else's public reaction opens read-only, on its page (with Add to paper).
          () => void navigate(publicReactionPath(id))
        );
      }),
    [
      confirmBeforeReplacing,
      dismissOcsrSourceImage,
      loadReactionDetails,
      navigate,
      openReaction,
      paperSize,
      replaceDrawingDocument
    ]
  );

  // #811 -- opening a drawing from search: your own loads into the editor; someone else's public
  // drawing opens read-only (only its owner can change it), with Add to my paper.
  const openDrawing = useCallback(
    async (id: string) => {
      const drawing = await getMoleculeDrawing(id).catch(() => null);
      if (drawing && drawing.owned === false) setViewingDocument({ kind: 'drawing', id });
      else {
        confirmBeforeReplacing('open', () => {
          dismissOcsrSourceImage();
          void persistence.loadDrawingById(id);
        });
      }
    },
    [confirmBeforeReplacing, dismissOcsrSourceImage, persistence]
  );

  // #463: a suggestion whose only openable place is one of the user's drawings opens it straight
  // into the editor, reusing the same handler ResultsRegion already uses; anything
  // else (#688: a bare molecule, or one found in several places) commits the full search instead.
  // #746: a reaction suggestion (found by its own words) is itself the one place: it opens onto
  // the paper, as its result card's Open does.
  const selectSuggestion = useCallback(
    (card: ResultEntry) => {
      if (card.kind === 'reaction') {
        void openReactionOnPaper(card.reactionId);
        return;
      }
      // #794 -- a drawing card is itself the one place.
      if (card.kind === 'drawing') {
        void openDrawing(card.drawingId);
        return;
      }
      if (card.kind === 'document') {
        setCommittedTextResults(textSearch.currentResults);
        return;
      }
      const { drawings, reactions, illustrations } = card.containers;
      const onlyDrawing = drawings.length === 1 && reactions.length === 0 && illustrations.length === 0;
      if (onlyDrawing) void openDrawing(drawings[0]!.id);
      else setCommittedTextResults(textSearch.currentResults);
    },
    [openDrawing, openReactionOnPaper, persistence, textSearch.currentResults]
  );

  const replaceWithNewDrawing = useCallback(
    (next: DrawingDocument | null) => {
      if (next) doc.replaceDocument(next, paperSize);
      else {
        doc.resetDocument(paperSize);
        dismissOcsrSourceImage();
      }
      persistence.resetForNewDrawing();
      doc.setTool('select');
      setToolbarSaveMessage(null);
      setPendingReplacement(null);
    },
    [
      doc.replaceDocument,
      doc.resetDocument,
      doc.setTool,
      persistence.resetForNewDrawing,
      paperSize,
      dismissOcsrSourceImage
    ]
  );

  const requestNewDrawing = useCallback(
    (next: DrawingDocument | null) => confirmBeforeReplacing('new', () => replaceWithNewDrawing(next)),
    [confirmBeforeReplacing, replaceWithNewDrawing]
  );
  const handleOcsrRecognized = useCallback(
    (document: DrawingDocument, sourceFile: File) =>
      confirmBeforeReplacing('new', () => {
        replaceWithNewDrawing(document);
        setOcsrSourceImage((prev) => {
          if (prev?.url) URL.revokeObjectURL(prev.url);
          return {
            url: URL.createObjectURL(sourceFile),
            name: sourceFile.name
          };
        });
        setOcsrImageExpanded(true);
      }),
    [confirmBeforeReplacing, replaceWithNewDrawing]
  );
  const ocsrImport = useOcsrImport(
    paperSize,
    handleOcsrRecognized,
    `${doc.document.structure.id}:${persistence.remoteDrawing?.id ?? ''}:${persistence.remoteReaction?.id ?? ''}`,
    fileDrawerOpen && (!importTarget || importTarget === 'image')
  );
  const startNewDrawing = useCallback(() => {
    ocsrImport.reset();
    dismissOcsrSourceImage();
    requestNewDrawing(null);
  }, [dismissOcsrSourceImage, ocsrImport.reset, requestNewDrawing]);

  // The File drawer already reports saved revisions; direct toolbar saves need their own confirmation.
  useEffect(() => {
    if (!toolbarSaveRequested) return;
    if (persistence.persistence.state === 'saving' || persistence.persistence.state === 'loading') return;
    if (persistence.remoteDrawing && !persistence.hasUnsavedChanges) {
      setToolbarSaveMessage(
        t('persistencePanel.statusLine', {
          title: persistence.remoteDrawing.savedTitle || t('persistencePanel.untitledDrawing'),
          version: persistence.remoteDrawing.documentRevision,
          archived: '',
          unsaved: t('persistencePanel.savedSuffix')
        })
      );
      setToolbarSaveRequested(false);
    } else if (persistence.persistence.state === 'error' || persistence.persistence.state === 'success') {
      setToolbarSaveRequested(false);
    }
  }, [
    toolbarSaveRequested,
    persistence.persistence.state,
    persistence.remoteDrawing,
    persistence.hasUnsavedChanges,
    t
  ]);
  useEffect(() => {
    if (!toolbarSaveMessage) return;
    const timer = window.setTimeout(() => setToolbarSaveMessage(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toolbarSaveMessage]);

  const closeFileDrawer = useCallback(() => {
    setFileDrawerOpen(false);
    const target = fileReturnFocus.current;
    if (target?.isConnected && target.offsetParent !== null) target.focus();
    else if (fileButtonRef.current?.offsetParent) fileButtonRef.current.focus();
    else moreTrigger?.focus();
  }, [moreTrigger]);

  // Bond drawing config (colour / standard length / angle interval / proposal radius), edited
  // via BondSettingsPanel in the EDIT tab -- lifted here as ChemicalStructureEditor holds it.
  const updateBondNumericConfig = useCallback((key: keyof BondDrawingConfig, value: string, minimum: number) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return;
    setBondConfig((current) => ({ ...current, [key]: Math.max(minimum, parsed) }));
  }, []);

  // JSON / SVG import + export in the File drawer (P10a-4), mirroring ChemicalStructureEditor.
  const [jsonImportError, setJsonImportError] = useState<string | null>(null);
  const exportJson = useCallback(() => {
    // #648 -- in Markush mode the export is the chem-drawing.v11 Markush document (R atoms as
    // placeholders), not the carbon-bearing editing copy.
    // #1189: use the resolved export file stem with extension .json.
    const fileName = `${exportFileStem}.json`;
    if (doc.markushDocument) {
      downloadText(fileName, JSON.stringify(doc.markushDocument, null, 2), 'application/json');
      return;
    }
    downloadText(fileName, JSON.stringify(doc.document, null, 2), 'application/json');
  }, [doc.document, doc.markushDocument, exportFileStem]);

  // #648 -- the R-group tool's click, with a brief visible/announced notice when it can't apply.
  const [markushNotice, setMarkushNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!markushNotice) return;
    const timeout = globalThis.setTimeout(() => setMarkushNotice(null), 4000);
    return () => globalThis.clearTimeout(timeout);
  }, [markushNotice]);
  const [markushReveal, setMarkushReveal] = useState<RGroupRevealRequest>();
  const revealMarkushOptions = useCallback(
    (label: string) => {
      showFilters();
      setMarkushReveal((current) => ({ label, sequence: (current?.sequence ?? 0) + 1 }));
    },
    [showFilters]
  );
  const { labelAtom, markushLayers } = doc;
  const labelMarkushAtom = useCallback(
    (atomId: string, label: string) => {
      const removing = markushLayers.variablePositions.some(
        (position) =>
          (position.kind === 'r-group' || position.kind === 'attachment-point') &&
          position.atomId === atomId &&
          position.rGroupLabel === label
      );
      const applied = labelAtom(atomId, label);
      setMarkushNotice(applied ? null : t('workspace.rail.markush.notTerminal'));
      if (applied && !removing) revealMarkushOptions(label);
    },
    [labelAtom, markushLayers.variablePositions, revealMarkushOptions, t]
  );
  const { attachToRingAt } = doc;
  const attachMarkushRing = useCallback(
    (point: NormalizedPoint, label: string) => {
      const applied = attachToRingAt(point, label);
      setMarkushNotice(applied ? null : t('workspace.rail.markush.notRing'));
      if (applied) revealMarkushOptions(label);
    },
    [attachToRingAt, revealMarkushOptions, t]
  );
  const { setAtomList } = doc;
  const applyMarkushAtomList = useCallback(
    (atomId: string, allowed: readonly number[]) => {
      setMarkushNotice(setAtomList(atomId, allowed) ? null : t('workspace.rail.markush.atomListRefused'));
    },
    [setAtomList, t]
  );
  // #663 -- Markush import (CXSMILES / molfile / RGfile): opens the result in Markush mode, asking
  // first when it would replace a drawing; resolves with the features that were skipped.
  const { openMarkushDrawing } = doc;
  const importMarkushText = useCallback(
    async (text: string): Promise<readonly string[] | null> => {
      const hasContent = doc.document.structure.graph.nodes.length > 0 || doc.document.annotations.length > 0;
      const { document, skipped } = await importMarkush(text);
      if (hasContent && !globalThis.confirm(t('markushImportPanel.replaceConfirm'))) return null;
      dismissOcsrSourceImage();
      openMarkushDrawing(document);
      return skipped;
    },
    [dismissOcsrSourceImage, doc.document, openMarkushDrawing, t]
  );

  // #656 -- the repeat-unit count pop-up: which unit, and the client point it opens at. A unit made
  // from the rail opens at its (just rendered) count label.
  const [repeatEditor, setRepeatEditor] = useState<{
    readonly id: string;
    readonly x: number;
    readonly y: number;
  } | null>(null);
  const [pendingRepeatId, setPendingRepeatId] = useState<string | null>(null);
  useEffect(() => {
    if (!pendingRepeatId) return;
    const frame = requestAnimationFrame(() => {
      const label = document.querySelector(`[data-repeat-unit-label="${CSS.escape(pendingRepeatId)}"]`);
      const rect = label?.getBoundingClientRect();
      if (rect) setRepeatEditor({ id: pendingRepeatId, x: rect.left, y: rect.bottom });
      setPendingRepeatId(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingRepeatId]);
  const { bracketAtoms } = doc;
  const bracketSelection = useCallback(() => {
    const id = bracketAtoms(doc.selection.atomIds);
    if (id) setPendingRepeatId(id);
  }, [bracketAtoms, doc.selection.atomIds]);
  const openRepeatEditor = useCallback((id: string, point: Readonly<{ x: number; y: number }>) => {
    setRepeatEditor({ id, x: point.x, y: point.y });
  }, []);
  const closeRepeatEditor = useCallback(() => setRepeatEditor(null), []);
  const repeatEditorCounts = repeatEditor ? doc.markushRepeatCounts.get(repeatEditor.id) : undefined;

  // ADR 0022 (as amended) -- the bolts the paper draws between a salt's ions; nothing stored.
  const saltIonicPairs = useMemo(() => ionicPairs(doc.document), [doc.document]);
  const paperHaptoLigands = useMemo(() => haptoLigands(doc.document), [doc.document]);
  const paperAgosticStarts = useMemo(() => agosticStarts(doc.document), [doc.document]);
  // #650 -- invalid Markush positions join chemical validation's red atom highlight. #1099: the
  // server check's errors replace the browser's preview once they are for this drawing.
  const checkedAtomIds = structureCheck.marks?.errorAtomIds ?? validation.invalidAtomIds;
  const invalidAtomIds = useMemo(
    () =>
      doc.markushIssueAtomIds.size === 0 ? checkedAtomIds : new Set([...checkedAtomIds, ...doc.markushIssueAtomIds]),
    [doc.markushIssueAtomIds, checkedAtomIds]
  );
  // ADR 0020 D5 / D6 (#1101): what the Checks list's buttons do.
  const { recheck } = structureCheck;
  // Owner test: "It's intended" only appeared later -- the check had answered before the structure
  // was registered, so the caller wasn't its author yet. Each registration that answers asks again.
  const registeredCount = drawnRegistration.registered;
  useEffect(() => {
    if (registeredCount > 0) recheck();
  }, [recheck, registeredCount]);
  const structureCheckActions = useMemo(() => {
    const request = (issue: StructureIssue) => ({
      molecule: toChemMoleculeV1(doc.chemistryDocument),
      rule: issue.rule,
      args: issue.args,
      atomId: issue.atomIds[0] ?? ''
    });
    return {
      onPreviewFix: (fix: StructureFix) => {
        setFixPreviewBase(settledChemistryRef.current);
        doc.previewStructureFix(fix);
      },
      onCancelFix: () => {
        doc.cancelStructureFix();
        setFixPreviewBase(null);
      },
      onApplyFix: (fix: StructureFix) => {
        doc.applyStructureFix(fix);
        setFixPreviewBase(null);
      },
      onConfirm: (issue: StructureIssue) => void confirmStructureWarning(request(issue)).then(recheck, recheck),
      onWithdraw: (issue: StructureIssue) => void withdrawStructureConfirmation(request(issue)).then(recheck, recheck)
    };
  }, [doc, recheck]);
  // #1110: the problems Structure info lists -- the server's once it has answered, else the browser's.
  const serverCheckPending =
    signedIn && doc.markushDocument === null && hasNodes && !structureCheck.failed && structureCheck.marks === null;
  const drawingProblemList = useMemo(
    () =>
      drawingProblems(
        structureCheck.marks?.issues ?? null,
        serverCheckPending,
        validation.currentValidation?.issues ?? null
      ),
    [structureCheck.marks, serverCheckPending, validation.currentValidation]
  );
  const { runValidation } = validation;
  const retryChecks = useCallback(() => {
    void runValidation();
    recheck();
  }, [runValidation, recheck]);
  // Owner test (#1101): the Arrange tab shows how many errors and warnings are open, so the Checks
  // list is found without selecting anything.
  const checksBadge = useMemo(() => {
    const open = (structureCheck.marks?.issues ?? []).filter(
      (issue) => issue.severity !== 'INFO' && !issue.confirmation
    );
    return open.length
      ? {
          count: open.length,
          severity: open.some((issue) => issue.severity === 'ERROR') ? ('error' as const) : ('warning' as const)
        }
      : undefined;
  }, [structureCheck.marks]);
  // ADR 0020 D3: each marked atom's reason (its most severe issue first), as the paper's tooltip.
  const markReasons = useMemo(() => {
    const reasons = new Map<string, string>();
    for (const issue of structureCheck.marks?.issues ?? []) {
      if (issue.severity === 'INFO') continue;
      if (issue.confirmation) continue;
      for (const id of issue.atomIds)
        if (!reasons.has(id)) reasons.set(id, `${structureIssueMessage(t, issue)}\n${t('structureCheck.whereToLook')}`);
    }
    return reasons;
  }, [structureCheck.marks, t]);
  // #758 -- the export dialog renders through the same pipeline the old direct Export SVG used,
  // bound here to the workspace's bond/stereo/Markush inputs.
  const [exportOpen, setExportOpen] = useState(false);
  const renderExportSvg = useCallback(
    (options: SvgRenderOptions) =>
      drawingDocumentToSvg(
        doc.document,
        {
          bondOrders: doc.bondOrders,
          aromaticBondIds: doc.aromaticBondIds,
          stereoBonds: doc.stereoBonds,
          overriddenBondIds: doc.overriddenBondIds
        },
        // #494 -- an exported SVG must be self-contained (no CSS var reference survives outside
        // this app's stylesheet), so a still-theme-following null falls back to a concrete color
        // here rather than in the shared default.
        bondConfig.color ?? DEFAULT_INK_COLOR,
        doc.coordinateSize,
        '',
        doc.markushAtomLabels,
        doc.markushLayers.variablePositions.flatMap((position) =>
          position.kind === 'attachment-point' ? [{ atomId: position.atomId, ringAtomIds: position.ringAtomIds }] : []
        ),
        doc.markushRepeatUnits,
        // #1141 (ADR 0025) -- the paper's "+" signs (#739) too, so the export is the paper as seen;
        // a selection is part of the drawing, without them.
        // #780 (owner): R/S and E/Z labels shown on the paper are in the export too.
        options.include
          ? { ...options, stereoLabels: showStereoLabels }
          : {
              ...options,
              stereoLabels: showStereoLabels,
              plusSigns: PLUS_JOINED_ZONES.flatMap((zone) =>
                plusSignPositions(doc.reactionMolecules.filter((molecule) => molecule.zone === zone))
              )
            }
      ),
    [
      showStereoLabels,
      doc.reactionMolecules,
      doc.aromaticBondIds,
      doc.bondOrders,
      doc.coordinateSize,
      doc.document,
      doc.markushAtomLabels,
      doc.markushLayers,
      doc.markushRepeatUnits,
      doc.overriddenBondIds,
      doc.stereoBonds,
      bondConfig.color
    ]
  );
  const exportSource = useMemo(
    () => ({ document: doc.document, selection: doc.selection, renderSvg: renderExportSvg }),
    [doc.document, doc.selection, renderExportSvg]
  );
  useLayoutEffect(() => {
    clipboardSource.current = exportSource;
  }, [exportSource]);
  const [printBusy, setPrintBusy] = useState(false);
  const [printError, setPrintError] = useState(false);
  const printDrawing = async () => {
    if (printBusy) return;
    setPrintBusy(true);
    setPrintError(false);
    let url: string | undefined;
    try {
      // Printing is a sheet of paper: the whole page, as drawn on it (#1140 keeps it so).
      const result = await prepareExport(exportSource, { ...defaultSettings('pdf'), scope: 'page' });
      url = URL.createObjectURL(new Blob([result.svg], { type: 'image/svg+xml' }));
      const content = document.createElement('div');
      const image = document.createElement('img');
      image.src = url;
      image.className = 'print-drawing';
      content.append(image);
      await printElement(content, t('printing.drawing'));
      // The print frame owns a loaded snapshot, so the source URL can be released.
    } catch {
      setPrintError(true);
    } finally {
      if (url) URL.revokeObjectURL(url);
      setPrintBusy(false);
    }
  };
  const importJson = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      try {
        const parsed = JSON.parse(await file.text()) as unknown;
        // #648 -- a file saved by Export JSON in Markush mode reopens in Markush mode.
        if (isMarkushDrawingJson(parsed)) doc.openMarkushDrawing(parsed);
        else doc.replaceDocument(validateDrawingDocument(parsed));
        dismissOcsrSourceImage();
        setJsonImportError(null);
      } catch (error) {
        setJsonImportError(error instanceof Error ? error.message : t('app.jsonImportError'));
      }
    },
    [dismissOcsrSourceImage, doc.openMarkushDrawing, doc.replaceDocument, t]
  );

  const persistenceBusy = persistence.persistence.state === 'loading' || persistence.persistence.state === 'saving';

  const handlePaperSizeChange = useCallback(
    (size: PaperSize) => {
      setPaperSize(size);
      doc.handlePaperSizeChange(size);
    },
    [doc.handlePaperSizeChange]
  );

  // Auto Layout (the `✦` action): recompute a fresh 2D layout for the structure and redraw,
  // keeping the graph, chemistry, and stereochemistry. #1227: cleanUpStructures regularizes
  // 2D coordinates in place component-by-component, preserving all non-molecule artifacts
  // (reaction arrows, reaction conditions, text annotations, curved mechanism arrows, shapes)
  // and their spatial arrangement across the paper.
  const [autoLayoutBusy, setAutoLayoutBusy] = useState(false);
  const autoLayout = useCallback(async () => {
    if (doc.document.structure.graph.nodes.length === 0) return;
    setAutoLayoutBusy(true);
    const snapshot = doc.document;
    try {
      const hasAtomSelection = doc.selection.atomIds.length > 0 || doc.selection.bondIds.length > 0;
      const selection = hasAtomSelection
        ? doc.selection
        : { atomIds: snapshot.structure.graph.nodes.map((n) => n.id), bondIds: [] };
      const cleaned = await cleanUpStructures(snapshot, selection);
      doc.commitDocument((current) => (current === snapshot ? cleaned : current));
    } catch (error) {
      console.error('Auto Layout failed', error);
    } finally {
      setAutoLayoutBusy(false);
    }
  }, [doc.document, doc.selection, doc.commitDocument]);

  const backToEditor = useCallback(() => {
    returningFromComposer.current = true;
    setPanelTab('compose');
    setViewMode('editor');
  }, []);

  const closeViewer = useCallback(() => {
    returningFromComposer.current = true;
    setViewingDocument(null);
  }, []);

  // Close whichever mobile drawer is open on Escape (#762: an overlay in the escape arbiter).
  useEscapeAction(
    ESCAPE_PRIORITY.overlay,
    () => {
      setMobileDrawer(null);
      return true;
    },
    mobileDrawer !== null
  );

  // Focus follows the composer/viewer swap (P10c): into the region on entry, back to the
  // COMPOSE tab on return.
  useEffect(() => {
    if (viewMode !== 'editor' || viewingDocument) {
      composerRef.current?.focus();
    } else if (returningFromComposer.current) {
      returningFromComposer.current = false;
      composeTabRef.current?.focus();
    }
  }, [viewMode, viewingDocument]);

  if (viewingDocument) {
    return (
      <main
        className="workspace-composer"
        data-testid="workspace-composer"
        ref={composerRef}
        tabIndex={-1}
        aria-label={t('workspace.composer.label')}
      >
        <SiteHeader />
        {viewingDocument.kind === 'drawing' ? (
          <DrawingViewer
            drawingId={viewingDocument.id}
            onBack={closeViewer}
            onAddToPaper={
              paperAdditions
                ? () => {
                    const id = viewingDocument.id;
                    closeViewer();
                    void paperAdditions.addDrawing(id);
                  }
                : undefined
            }
          />
        ) : (
          <IllustrationViewer illustrationId={viewingDocument.id} onBack={closeViewer} />
        )}
      </main>
    );
  }

  // #807 R3 -- the formula (or Markush chip), the match count and Graph stats: in the top bar, beside
  // the account (they used to end the paper toolbar). The count also badges the collapsed info button.
  const topBarInfo = (
    <>
      <DocumentBar doc={doc} />
      {myDrawingsActive && myDrawings.totalElements !== null ? (
        <span className="workspace-match-count" data-testid="my-drawings-badge">
          {t(myDrawingsStatus === 'archived' ? 'myDrawings.badgeArchived' : 'myDrawings.badge', {
            count: myDrawings.totalElements
          })}
        </span>
      ) : null}
      {searchMode === 'drawing' && reactionSearch.active && arrowOnly ? (
        // #743: with the arrow alone on the paper the results are reactions only; count those.
        reactionSearch.loading || reactionSearch.failed ? null : (
          <span className="workspace-match-count" data-testid="match-count-badge">
            {t('workspace.documentBar.matchCount', { count: reactionSearch.hits.length })}
          </span>
        )
      ) : (
        <MatchCountBadge
          search={activeSearch}
          visibleCount={filters.visibleCount}
          hiddenCount={filters.hiddenCount}
          foldedCount={filters.foldedCount}
          onShowFilters={showFilters}
          // #701 -- once a drawing search's answers have expired, a refresh fetches them afresh.
          onRefresh={searchMode === 'drawing' && search.resultsExpired ? () => void search.runSearch() : undefined}
        />
      )}
      <FeedbackDisclosure
        metrics={doc.metrics}
        hasGraphNodes={hasNodes}
        problems={drawingProblemList}
        properties={validation.currentValidation?.properties ?? null}
        failed={validation.validation.state === 'error' && !validation.validationIsStale}
        onRetry={retryChecks}
        onShowChecks={showChecks}
        actions={structureCheckActions}
      />
    </>
  );
  const topBarInfoCount: number | null =
    myDrawingsActive && myDrawings.totalElements !== null
      ? myDrawings.totalElements
      : searchMode === 'drawing' && reactionSearch.active && arrowOnly
        ? reactionSearch.loading || reactionSearch.failed
          ? null
          : reactionSearch.hits.length
        : activeSearch.currentResults !== null && filters.hasResults
          ? filters.visibleCount
          : null;

  if (viewMode !== 'editor') {
    return (
      <main
        className="workspace-composer"
        data-testid="workspace-composer"
        ref={composerRef}
        tabIndex={-1}
        aria-label={t('workspace.composer.label')}
      >
        <SiteHeader />
        {viewMode === 'illustration' ? (
          <IllustrationComposer search={search} paperSize={paperSize} onBack={backToEditor} />
        ) : (
          <BiopolymerComposer search={search} onBack={backToEditor} />
        )}
      </main>
    );
  }

  return (
    <div
      className="workspace-shell"
      data-testid="workspace-shell"
      data-drawer={mobileDrawer ?? undefined}
      data-rail-collapsed={railCollapsed || undefined}
      data-right-collapsed={rightPanelCollapsed || undefined}
      onClickCapture={guardLinksAway}
    >
      <TopBar
        info={topBarInfo}
        infoCount={topBarInfoCount}
        infoProblems={drawingProblemList?.length ?? 0}
        mode={searchMode}
        onModeChange={setSearchMode}
        query={textQuery}
        onQueryChange={(value) => {
          setTextQuery(value);
          // #485: a stale PubChem-import error/loading status must not keep blocking the
          // suggestion list once the user has moved on to a different query.
          setPubchemImportFromSearch(false);
          smilesImport.resetSmilesImportState();
        }}
        suggestions={searchMode === 'text' ? suggestions : []}
        onSelectSuggestion={selectSuggestion}
        onCommitSearch={commitTextSearch}
        pubchemFallback={searchMode === 'text' ? textSearch.pubchemFallback : null}
        onSelectPubChem={selectPubChemFallback}
        // #485: while typing, the live dropdown previously showed nothing at all -- no spinner,
        // no error -- whenever there were zero suggestions yet, which is indistinguishable from a
        // hang. searchStatus lets it render a loading/error row even then.
        searchStatus={searchMode === 'text' ? textSearch.search : { state: 'idle' }}
        onRetrySearch={retryTextSearch}
        // #485: selectPubChemFallback below imports the clicked result's SMILES, which can fail
        // (e.g. an element PubChem's own SMILES uses but this editor doesn't support -- see
        // smilesImport.ts's validateImportedAtom) with no visible surface until now -- see
        // TopBar's pubchemImportStatus handling. Gated on the flag above so a stale error from the
        // File drawer's own SMILES/PubChem/OCSR import panels (same shared smilesImportState)
        // doesn't flash here just because this field regains focus.
        pubchemImportStatus={pubchemImportFromSearch ? smilesImport.smilesImportState : { state: 'idle' }}
      />
      <section className="workspace-toolbar" aria-label={t('workspace.actionBar.label')}>
        <button
          type="button"
          className="icon-button workspace-drawer-toggle workspace-drawer-toggle--tools"
          aria-label={t('workspace.rail.label')}
          aria-expanded={mobileDrawer === 'rail'}
          onClick={() => setMobileDrawer((current) => (current === 'rail' ? null : 'rail'))}
          data-testid="open-tools-drawer"
        >
          {/* #405 — the same chevron the desktop rail uses: points toward where the panel
                  opens when closed, back toward its edge when open. */}
          <Icon name={mobileDrawer === 'rail' ? 'chevronLeft' : 'chevronRight'} />
        </button>
        {/* #508 -- same grouping trick as .workspace-toolbar-end below: keeps the action bar
                and the (portaled) viewport controls next to each other as one cluster, rather
                than .workspace-toolbar's space-between spreading them across the row. */}
        <div className="workspace-toolbar-start" ref={toolbarRowRef}>
          <EditorActionBar
            doc={doc}
            partialCharges={partialCharges}
            onAutoLayout={autoLayout}
            autoLayoutBusy={autoLayoutBusy}
            onOpenFile={() => {
              fileReturnFocus.current = document.activeElement as HTMLElement;
              setImportTarget(null);
              setFileDrawerOpen(true);
            }}
            onImport={(target) => {
              fileReturnFocus.current = document.activeElement as HTMLElement;
              setImportTarget(target);
              setFileDrawerOpen(true);
            }}
            onSave={() => {
              setToolbarSaveMessage(null);
              if (!persistence.remoteDrawing && !persistence.remoteReaction) setSaveNameOpen(true);
              else {
                setToolbarSaveRequested(true);
                void saveCurrentDrawing();
              }
            }}
            saveVisible={!doc.hasMarkushContent && persistence.hasUnsavedChanges && (hasNodes || doc.hasReactionArrow)}
            saveBusy={persistenceBusy}
            saveReadOnly={tenant.roleIn(persistence.openTenantId) === 'VIEWER'}
            onExportImage={() => {
              exportReturnFocus.current = document.activeElement as HTMLElement;
              setExportOpen(true);
            }}
            onExportJson={exportJson}
            onPrint={() => void printDrawing()}
            printBusy={printBusy}
            fileButtonRef={fileButtonRef}
            hiddenGroups={hiddenToolbarGroups}
            moreOpen={moreOpen}
            onMoreOpenChange={setMoreOpen}
            onMoreTrigger={setMoreTrigger}
            overflowSlotRef={setOverflowSlot}
            viewControls={
              // #508 -- portal target for PaperViewportControls; display: contents so it
              // doesn't introduce its own box in this flex row. Empty until DrawingPaper mounts
              // and portals its controls in. #807 -- inside the action bar, after the pointer
              // modes, so the row reads in the owner-approved order.
              <div className="workspace-viewport-controls-slot" ref={setViewportControlsSlot} />
            }
          />
        </div>
        {/* #648 / #807 R3 -- the Markush tools' short-lived message, beside the paper it is about. */}
        <MarkushNotice message={markushNotice} />
        <button
          type="button"
          className="icon-button workspace-drawer-toggle workspace-drawer-toggle--panel"
          aria-label={t('workspace.panel.label')}
          aria-expanded={mobileDrawer === 'panel'}
          onClick={() => setMobileDrawer((current) => (current === 'panel' ? null : 'panel'))}
          data-testid="open-panel-drawer"
        >
          <Icon name={mobileDrawer === 'panel' ? 'chevronRight' : 'chevronLeft'} />
        </button>
      </section>
      {mobileDrawer ? (
        <div
          className="workspace-mobile-backdrop"
          data-testid="mobile-drawer-backdrop"
          role="presentation"
          onMouseDown={() => setMobileDrawer(null)}
        />
      ) : null}
      <div className="workspace-body">
        <ToolRail
          tool={doc.tool}
          setTool={doc.setTool}
          doc={doc}
          charge={railCharge}
          chargeLive={liveChargeAtomId !== undefined}
          onChargeChange={changePendingCharge}
          onOpenPeriodicTable={() => {
            // #411 -- the periodic table is a full-screen modal; on mobile it is opened from the
            // rail drawer, so close that drawer as we hand over to the table.
            setMobileDrawer(null);
            setPeriodicTableOpen(true);
          }}
          bracket={{ enabled: doc.canBracket(doc.selection.atomIds), onBracket: bracketSelection }}
          onOpenAtomList={() => {
            setMobileDrawer(null);
            setAtomListDraft(atomListFromTool(doc.tool) ?? []);
            setPeriodicTableOpen(true);
          }}
          onLoadTemplate={(id) => confirmBeforeReplacing('open', () => void persistence.loadDrawingById(id))}
          onLoadMoleculeTemplate={(id) => void loadMoleculeTemplate(id)}
          templateLoadDisabled={persistenceBusy}
          collapsed={railCollapsed}
          setCollapsed={setRailCollapsedContextual}
        />
        <main
          className="workspace-center"
          data-testid="workspace-center"
          aria-label={t('workspace.center.label')}
          style={paperSpanStyle}
        >
          {/* #700: the result cards and My drawings add to the paper through this context. */}
          <PaperAdditionsContext.Provider value={paperAdditions}>
            <ResultsRegion
              paper={
                // A plain wrapper div, not a new DrawingPaper prop: the wrapper carries the
                // mosaic's grid-span sizing (`.workspace-paper-cell`, styles.css), and
                // DrawingPaper's own ResizeObserver (usePaperRenderers.ts) already reports
                // whatever pixel size that wrapper resolves to -- zoom/pan/fit are entirely
                // decoupled from how the wrapper is sized, so this needed no changes there.
                <div
                  ref={setPaperDropNode}
                  className="workspace-paper-cell"
                  data-drop-candidate={paperCanDrop || undefined}
                  data-drop-over={paperDropOver || undefined}
                >
                  <DrawingPaper
                    clientPointRef={clientToPaper}
                    onPointerPositionChange={doc.updatePastePosition}
                    onPasteAt={doc.pasteClipboardAt}
                    canPaste={doc.hasClipboard}
                    lines={doc.lines}
                    atoms={doc.atoms}
                    bondOrders={doc.bondOrders}
                    armedBondTool={doc.armedBondTool}
                    armedBondOrder={
                      doc.armedBondTool.kind === 'order'
                        ? doc.armedBondTool.order
                        : doc.armedBondTool.kind === 'crossed-double'
                          ? 2
                          : 1
                    }
                    aromaticBondIds={doc.aromaticBondIds}
                    aromaticRingCenters={doc.aromaticCenters}
                    annotations={doc.annotationRenderables}
                    stereoBonds={doc.stereoBonds}
                    atomAppearance={doc.document.depiction.atomAppearance}
                    bondAppearance={doc.document.depiction.bondAppearance}
                    cycleFills={doc.document.depiction.cycleFills}
                    tool={doc.tool}
                    selection={doc.selection}
                    invalidAtomIds={invalidAtomIds}
                    invalidBondIds={structureCheck.marks?.errorBondIds ?? validation.invalidBondIds}
                    {...(structureCheck.marks
                      ? {
                          warningAtomIds: structureCheck.marks.warningAtomIds,
                          warningBondIds: structureCheck.marks.warningBondIds,
                          markReasons
                        }
                      : {})}
                    onSelectionChange={doc.setSelection}
                    onAtomElementChange={(id, z) => doc.changeAtomElement(id, z, pendingCharge ?? undefined)}
                    onAtomChargeApply={(id) => doc.applyAtomFormalCharge(id, pendingCharge ?? 0)}
                    onAtomPlace={(point, z) => doc.placeAtom(point, z, pendingCharge ?? undefined)}
                    // #674: the Markush tools work on any drawing (there is no Markush mode).
                    onAtomGroup={(atomId, key) => void doc.placeFunctionalGroup(key, { atomId })}
                    onPaperGroup={(point, key) => void doc.placeFunctionalGroup(key, { point })}
                    onAtomRGroupLabel={labelMarkushAtom}
                    onPaperRGroupLabel={attachMarkushRing}
                    onAtomAtomList={applyMarkushAtomList}
                    markushRepeatUnits={doc.markushRepeatUnits}
                    onRepeatUnitActivate={openRepeatEditor}
                    markushAttachmentLines={doc.markushAttachmentLines}
                    {...(reactionZones
                      ? { reactionZones, reactionZoneRefs: boxRefs, onReactionBoxMove: doc.moveReactionBox }
                      : {})}
                    onBondToolApply={doc.applyArmedBondTool}
                    onImplicitHydrogenDirectionChange={doc.updateImplicitHydrogenDirection}
                    onAtomsMove={doc.moveSelection}
                    onLinesChange={doc.handleLinesChange}
                    onRingPlace={doc.handleRingPlace}
                    onChainPlace={doc.handleChainPlace}
                    onShapePlace={doc.handleShapePlace}
                    onShapeChange={doc.handleShapeChange}
                    onArrowChange={doc.handleArrowChange}
                    onTextCommit={doc.handleTextCommit}
                    onTextEditEnd={doc.finishReactionText}
                    page={pageOf(doc.document)}
                    onPageChange={doc.setPage}
                    onAnnotationsMove={doc.moveSelectedAnnotations}
                    onUndo={doc.undo}
                    onRedo={doc.redo}
                    renderTextToolbar={renderTextToolbar}
                    onWebGlAvailabilityChange={setWebGlAvailable}
                    onPaperSizeChange={handlePaperSizeChange}
                    bondConfig={bondConfig}
                    bondAtomIds={Object.fromEntries(
                      doc.document.structure.graph.edges.map((edge) => [edge.id, [edge.source, edge.target] as const])
                    )}
                    haptoLigands={paperHaptoLigands}
                    agosticStarts={paperAgosticStarts}
                    showHapticity={showHapticity}
                    documentId={doc.document.structure.graph.id}
                    isOversizedImport={doc.isOversizedImport}
                    viewportControlsContainer={viewportControlsSlot}
                    viewHiddenGroups={hiddenViewGroups}
                    viewOverflowContainer={moreOpen ? overflowSlot : null}
                    viewOverflowAnchor={moreTrigger}
                    onViewOverflowClose={closeMore}
                    idleHint={searchMode === 'drawing' ? t('searchResultsPanel.idle') : undefined}
                    showNodeNumbers={showNodeNumbers}
                    stereoLabels={stereoLabels}
                    bondStereoLabels={bondStereoLabels}
                    showBondAcceptors={showBondAcceptors}
                    showCenterOfRotation={showCenterOfRotation}
                    selectedTransformAtomIds={doc.selectedTransformAtomIds}
                    rotationPivot={doc.rotationPivot}
                    onRotationPivotChange={doc.setRotationPivot}
                    lassoSelectionEnabled={lassoSelectionEnabled}
                    showIonicBonds={showIonicBonds}
                    ionicPairs={saltIonicPairs}
                  />
                  <PaperResizeHandle
                    span={shownPaperSpan}
                    onChange={paperSpan.changeSpan}
                    onMosaicMeasured={setMosaic}
                    maxColumns={mosaicColumns}
                  />
                </div>
              }
              search={activeSearch}
              // #1178: only a structure search's Identical cards mirror the paper.
              identicalPieces={activeSearch === search ? search.currentResults?.identicalPieces : undefined}
              showingPreviousResults={showingPreviousResults}
              filters={filters}
              myDrawings={myDrawingsActive ? myDrawings : undefined}
              onRestoreDrawing={myDrawingsStatus === 'archived' ? (drawing) => void restoreDrawing(drawing) : undefined}
              underPaper={
                doc.hasReactionArrow ? (
                  <ReactionConditionsCard
                    fields={doc.conditionFields}
                    onChange={doc.setConditionField}
                    fromText={textReading}
                  />
                ) : undefined
              }
              sourceImageCard={
                ocsrSourceImage ? (
                  <OcsrSourceImageCard
                    imageUrl={ocsrSourceImage.url}
                    imageName={ocsrSourceImage.name}
                    expanded={ocsrImageExpanded}
                    onToggleExpanded={() => setOcsrImageExpanded((expanded) => !expanded)}
                    onDismiss={dismissOcsrSourceImage}
                  />
                ) : undefined
              }
              reactionResults={
                // #743: while an arrow is on the paper, a reaction search runs -- the whole query when
                // the arrow is all there is (arrowOnly), else ahead of the molecules' results.
                searchMode !== 'drawing' || !reactionSearch.active
                  ? undefined
                  : reactionSearch.failed
                    ? { hits: [], formulaOf: reactionSearch.formulaOf, onRetry: reactionSearch.retry, arrowOnly }
                    : { ...reactionSearch, arrowOnly }
              }
              {...(searchMode === 'drawing' && (markushSearchNote || drawnRegistration.pending > 0)
                ? { note: markushSearchNote ?? t('drawnMolecules.pending', { count: drawnRegistration.pending }) }
                : {})}
              onOpen={(id, kind) => {
                // #523: a drawing match opens straight into the editor; a reaction/illustration match has no editor
                // representation to load into, so it opens a read-only viewer instead.
                // #705: a reaction opens onto the paper -- its arrow, boxes, conditions, catalysts.
                if (kind === 'reactions') void openReactionOnPaper(id);
                else if (kind === 'illustrations') setViewingDocument({ kind: 'illustration', id });
                else void openDrawing(id);
              }}
              openDisabled={persistenceBusy}
            />
          </PaperAdditionsContext.Provider>
        </main>
        <RightPanel
          doc={doc}
          structureIssues={structureCheck.marks?.issues ?? NO_ISSUES}
          checksBadge={checksBadge}
          structureCheckActions={structureCheckActions}
          partialCharges={partialCharges}
          filters={filters}
          search={search}
          tab={panelTab}
          onTabChange={setPanelTab}
          markushReveal={markushReveal}
          bondConfig={bondConfig}
          onBondColorChange={(color) => setBondConfig((current) => ({ ...current, color }))}
          onBondNumericChange={updateBondNumericConfig}
          onCompose={setViewMode}
          composeTabRef={composeTabRef}
          collapsed={rightPanelCollapsed}
          setCollapsed={setRightCollapsedContextual}
          showNodeNumbers={showNodeNumbers}
          onToggleShowNodeNumbers={setShowNodeNumbers}
          showStereoLabels={showStereoLabels}
          showIonicBonds={showIonicBonds}
          onToggleShowIonicBonds={setShowIonicBonds}
          showHapticity={showHapticity}
          onToggleShowHapticity={setShowHapticity}
          onToggleShowStereoLabels={setShowStereoLabels}
          showBondAcceptors={showBondAcceptors}
          onToggleShowBondAcceptors={setShowBondAcceptors}
          showCenterOfRotation={showCenterOfRotation}
          onToggleShowCenterOfRotation={setShowCenterOfRotation}
          lassoSelectionEnabled={lassoSelectionEnabled}
          onToggleLassoSelectionEnabled={setLassoSelectionEnabled}
          styleDefaultsStatus={styleDefaultsStatus}
          myDrawingsFilter={myDrawingsActive ? { status: myDrawingsStatus, onChange: setMyDrawingsStatus } : undefined}
          discussionCount={discussionCount.count}
          discussion={
            <WorkspaceDiscussionPanel
              drawing={persistence.remoteDrawing}
              structureMatchesSaved={persistence.structureMatchesSaved}
              selection={doc.selection}
              atoms={doc.atoms}
              onSelect={doc.setSelection}
              onDrawingCount={discussionCount.setDrawingCount}
              onMoleculeCount={discussionCount.setMoleculeCount}
            />
          }
        />
      </div>
      {!fileDrawerOpen &&
      !saveNameOpen &&
      (persistence.persistence.state === 'success' || persistence.persistence.state === 'error') ? (
        <p className="workspace-save-feedback" role={persistence.persistence.state === 'error' ? 'alert' : 'status'}>
          {persistence.persistence.message}
        </p>
      ) : null}
      {clipboardMessage ? (
        <p className="workspace-save-feedback" role={clipboardMessage.error ? 'alert' : 'status'}>
          {clipboardMessage.text}
        </p>
      ) : null}
      {unsavedDraft.offer ? (
        <UnsavedDraftBanner
          draft={unsavedDraft.offer}
          onRestore={() => confirmBeforeReplacing('open', () => void unsavedDraft.restoreOffer(restoreDraft))}
          onDiscard={unsavedDraft.discardOffer}
        />
      ) : null}
      {toolbarSaveMessage ? (
        <p className="workspace-save-feedback" role="status">
          {toolbarSaveMessage}
        </p>
      ) : null}
      {printError ? <p role="alert">{t('printing.error')}</p> : null}
      <FileDrawer
        open={fileDrawerOpen}
        importTarget={importTarget}
        onClose={closeFileDrawer}
        onSave={() => void saveCurrentDrawing()}
        persistence={persistence}
        smilesImport={smilesImport}
        pubChemImport={pubChemImport}
        ocsrImport={ocsrImport}
        onNewDrawing={startNewDrawing}
        onImportJson={(event) => void importJson(event)}
        onPrint={() => void printDrawing()}
        onExportJson={exportJson}
        onExportSvg={() => {
          exportReturnFocus.current = fileButtonRef.current?.offsetParent ? fileButtonRef.current : moreTrigger;
          setFileDrawerOpen(false);
          setExportOpen(true);
        }}
        jsonImportError={jsonImportError}
        markushDrawing={doc.hasMarkushContent}
        onImportMarkush={importMarkushText}
      />
      {pendingReplacement ? (
        <SaveDrawingDialog
          initialTitle={persistence.drawingTitle}
          heading={t('persistencePanel.saveBeforeNew')}
          description={t(
            pendingReplacement.reason === 'open'
              ? 'persistencePanel.saveBeforeOpenDescription'
              : pendingReplacement.reason === 'leave'
                ? 'persistencePanel.saveBeforeLeaveDescription'
                : 'persistencePanel.saveBeforeNewDescription'
          )}
          saveLabel={t('persistencePanel.saveAndContinue')}
          blocked={persistenceBusy}
          error={persistence.persistence.state === 'error' ? persistence.persistence.message : null}
          onClose={() => {
            setPendingReplacement(null);
            closeFileDrawer();
          }}
          onDiscard={() => {
            setPendingReplacement(null);
            pendingReplacement.proceed();
            closeFileDrawer();
          }}
          onSave={async (title) => {
            if (!(await saveCurrentDrawing(title))) return false;
            setPendingReplacement(null);
            pendingReplacement.proceed();
            return true;
          }}
        />
      ) : null}
      {saveNameOpen ? (
        <SaveDrawingDialog
          initialTitle={persistence.drawingTitle}
          error={persistence.persistence.state === 'error' ? persistence.persistence.message : null}
          onClose={() => setSaveNameOpen(false)}
          onSave={(title) => {
            setToolbarSaveRequested(true);
            return saveCurrentDrawing(title);
          }}
        />
      ) : null}
      {exportOpen ? (
        <ExportDialog
          source={exportSource}
          fileStem={exportFileStem}
          onClose={() => {
            setExportOpen(false);
            const target = exportReturnFocus.current;
            if (target?.isConnected && target.offsetParent !== null) target.focus();
            else moreTrigger?.focus();
          }}
        />
      ) : null}
      {repeatEditor && repeatEditorCounts ? (
        <RepeatUnitPopover
          x={repeatEditor.x}
          y={repeatEditor.y}
          countMin={repeatEditorCounts.countMin}
          countMax={repeatEditorCounts.countMax}
          onApply={(countMin, countMax) => doc.setRepeatCount(repeatEditor.id, countMin, countMax)}
          onRemove={() => {
            doc.removeRepeat(repeatEditor.id);
            setRepeatEditor(null);
          }}
          onClose={closeRepeatEditor}
        />
      ) : null}
      <PeriodicTablePanel
        open={periodicTableOpen}
        charge={pendingCharge ?? 0}
        onChargeChange={changePendingCharge}
        armedZ={doc.tool.startsWith('element-') ? Number(doc.tool.slice('element-'.length)) : null}
        onPick={(atomicNumber) => {
          // #409 — re-picking the armed element disarms it; any other pick re-arms. Either way
          // the panel closes, so the Elements button just needs to *open* it (never disarm).
          const armed = doc.tool === `element-${atomicNumber}` && pendingCharge === null;
          doc.setTool(armed ? 'draw' : `element-${atomicNumber}`);
          setPeriodicTableOpen(false);
        }}
        onClose={() => {
          setPeriodicTableOpen(false);
          setAtomListDraft(null);
        }}
        {...(atomListDraft !== null
          ? {
              multiSelect: {
                selected: atomListDraft,
                onToggle: (atomicNumber: number) =>
                  setAtomListDraft((current) =>
                    current?.includes(atomicNumber)
                      ? current.filter((z) => z !== atomicNumber)
                      : [...(current ?? []), atomicNumber]
                  ),
                onDone: () => {
                  doc.setTool(atomListTool(atomListDraft));
                  setPeriodicTableOpen(false);
                  setAtomListDraft(null);
                }
              }
            }
          : {})}
      />
    </div>
  );
}
